import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import OverflowDrawer from "@/app/shared/OverflowDrawer";
import { NEW_QUEUE } from "@/constants/New";
import urls from "@/constants/Urls";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputMedia from "@/design/forms/InputMedia";
import InputNumber from "@/design/forms/InputNumber";
import InputNumeric from "@/design/forms/InputNumeric";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viGear, viList, viMegaphone, viPeople } from "@sippy-platform/valkyrie";

import {
  Box,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  List,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import MemberItem from "./members/MemberItem";
import QueueMembers from "./members/QueueMembers";
import QueueCard from "./QueueCard";
import queueReducer from "./queueReducer";

function Queue() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);
  const musicOnHoldStore = useSelector((state) => state.domain.musicOnHolds);
  const soundStore = useSelector((state) => state.domain.sounds);

  const [tab, setTab] = useState(() => (id === "new" ? 1 : 0));
  const [previousId, setPreviousId] = useState(id);

  if (id !== previousId) {
    setPreviousId(id);
    setTab(id === "new" ? 1 : 0);
  }
  const handleTab = (event, newTab) => setTab(newTab);

  // Get general data
  useEffect(() => {
    Promise.all([Api.getMusicOnHolds(), Api.getSounds(), Api.getPossibleQueueMembers()]);
  }, []);

  const {
    value: queue,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_QUEUE,
    reducer: queueReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 120], { message: "Name must be at most 120 characters." }),
      exten: number()
        .required({ message: "Number is required." })
        .outside([100, 119], {
          message: "Number cannot be between 100 and 119.",
        })
        .not(0, { message: "Number cannot be 0." })
        .not(1711, { message: "Number cannot be 1711." })
        .not(1712, { message: "Number cannot be 1712." })
        .not(1722, { message: "Number cannot be 1722." })
        .not(1733, { message: "Number cannot be 1733." })
        .not(1813, { message: "Number cannot be 1813." }),
      timeout: number()
        .required({ message: "Time-out is required." })
        .min(1, { message: "Time-out must be between 1 and 120 seconds." })
        .max(120, { message: "Time-out must be between 1 and 120 seconds." }),
      retry: number()
        .min(0, { message: "Retry call must be between 0 and 60 seconds." })
        .max(60, { message: "Retry call must be between 0 and 60 seconds." }),
      weight: number()
        .min(1, { message: "Priority must be between 1 and 100 seconds." })
        .max(100, { message: "Priority must be between 1 and 100 seconds." }),
      periodicAnnounceFrequency: number().required({
        condition: queue.periodicAnnounce !== "0" && queue.periodicAnnounce !== 0 && queue.periodicAnnounce !== null,
        message: "Repeat must be between 0 and 10 times.",
      }),
      announceFrequency: number().required({
        condition: queue.announceHoldtime !== "no" || queue.announcePosition !== "no",
        message: "Announcement frequency is required.",
      }),
      minAnnounceFrequency: number().required({
        message: "Announcement frequency is required.",
      }),
      ringSeconds: number()
        .min(1, {
          message: "Ring seconds must be between 1 and 18000 seconds.",
        })
        .max(18000, {
          message: "Ring seconds must be between 1 and 18000 seconds.",
        }),
      announcePositionLimit: number().not(0, {
        message: "The position threshold should be a value equal or higher then 1 or empty.",
      }),
    }),
    onSubmit: () => Api.storeQueue(queue),
    onSuccess: (response) => {
      if (!queue.id) {
        dispatch({ type: "id", value: response.data.queue.id });
        navigate(`../${response.data.queue.id}`);
      }

      Promise.resolve(Api.getQueues());
    },
  });

  useEffect(() => {
    let ignore = false;

    if (Number(id) === queue.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      dispatch({ type: "set", value: NEW_QUEUE });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getQueue({ id: Number(id) }))
      .then((response) => {
        if (!ignore) {
          setTab(0);
          dispatch({ type: "set", value: response.data.queue });
        }
      })
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [dispatch, id, queue.id]);

  // Handle changes
  const formHandler = (property, value) => {
    dispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getQueues()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${queue.name || "Queue"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          tab={tab}
          changeTab={handleTab}
          tabs={[
            {
              label: "Overview",
              icon: viList,
              id: "queue",
              disabled: !queue.id,
            },
            { label: "Settings", icon: viGear, id: "settings" },
            { label: "Announcements", icon: viMegaphone, id: "announcements" },
            {
              label: "Members",
              icon: viPeople,
              id: "members",
              disabled: !queue.id,
            },
          ]}
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={queue.name} icon={viList} />
              </IconButton>
            ) : (
              <Avatar text={queue.name} icon={viList} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={queue.id || "nk"}
                isActionable={!!queue?.id}
                onDeleteCheck={() => Api.deleteQueue(queue, false)}
                onDelete={() => Api.deleteQueue(queue, true)}
                name={`${queue.name} (${queue.exten})`}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={queue.name || "Unnamed queue"}
          subheader={queue.exten || "No number set"}
        />
        {tab === 0 && <QueueCard queue={queue} />}
        {tab === 1 && (
          <>
            <FormGroup title="Identity">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText required id="name" label="Name" error={errors} onChange={formHandler} value={queue.name} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumber
                  required
                  id="exten"
                  label="Number"
                  error={errors}
                  onChange={formHandler}
                  value={queue.exten}
                  helperText="Number cannot be 0 or any Belgian emergency number."
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Settings">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputToggle
                  id="recording"
                  error={errors}
                  onChange={formHandler}
                  states={[false, true]}
                  value={queue.recording}
                  primary="Record calls"
                  secondary="Record calls that come through this queue."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputAutocomplete
                  id="musiconhold"
                  label="Music on hold"
                  onChange={formHandler}
                  value={queue.musiconhold || "Ringing"}
                  selectValue="name"
                  selects={[...musicOnHoldStore.list, { name: "Ringing" }]}
                  getOptionLabel={(option) => option.name}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputToggle
                  id="answeredElsewhere"
                  error={errors}
                  onChange={formHandler}
                  states={[0, 1]}
                  value={queue.answeredElsewhere}
                  primary="Disable missed call"
                  secondary="Don't show calls as unanswered if another member answered the call elsewhere on supported devices."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumeric
                  id="weight"
                  label="Priority"
                  onChange={formHandler}
                  value={queue.weight}
                  min={1}
                  max={100}
                  helperText="The queue with the highest priority (where 1 is the lowest priority) is preferred for members who are active in more then one queue."
                  error={errors}
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Call behavior">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputSelect
                  id="strategy"
                  label="Ring strategy"
                  error={errors}
                  onChange={formHandler}
                  value={queue.strategy}
                  selects={[
                    { value: "ringall", name: "Ring all" },
                    { value: "leastrecent", name: "Least recent" },
                    { value: "fewestcalls", name: "Fewest calls" },
                    { value: "random", name: "Random" },
                    { value: "rrmemory", name: "Linear" },
                  ]}
                  getOptionLabel={(option) => option.name}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumeric
                  id="retry"
                  label="Retry call"
                  error={errors}
                  onChange={formHandler}
                  value={queue.retry}
                  min={0}
                  max={60}
                  endAdornment={
                    <InputAdornment position="end" sx={{ marginRight: 1 }}>
                      seconds
                    </InputAdornment>
                  }
                  helperText="Time after a declined call will be tried again."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumeric
                  id="wrapUpTime"
                  label="Wrap-up time"
                  error={errors}
                  onChange={formHandler}
                  value={queue.wrapUpTime}
                  min={0}
                  endAdornment={
                    <InputAdornment position="end" sx={{ marginRight: 1 }}>
                      seconds
                    </InputAdornment>
                  }
                  helperText="Time after a successful call before a member is called again."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumeric
                  required
                  id="timeout"
                  label="Time-out"
                  error={errors}
                  onChange={formHandler}
                  value={queue.timeout}
                  min={1}
                  max={120}
                  endAdornment={
                    <InputAdornment position="end" sx={{ marginRight: 1 }}>
                      seconds
                    </InputAdornment>
                  }
                  helperText="Time the member's device will ring, maximum 120 seconds."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputToggle
                  id="ringInUse"
                  onChange={formHandler}
                  states={["no", "yes"]}
                  value={queue.ringInUse}
                  primary="Ring busy members"
                  secondary="Ring members that are already in a call."
                  error={errors}
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Callers">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumeric
                  id="ringSeconds"
                  label="Ring seconds"
                  error={errors}
                  onChange={formHandler}
                  value={queue.ringSeconds}
                  min={1}
                  max={18000}
                  endAdornment={
                    <InputAdornment position="end" sx={{ marginRight: 1 }}>
                      seconds
                    </InputAdornment>
                  }
                  helperText="Maximum time a caller can be in the queue, leave empty for unlimited."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumeric
                  id="maxlen"
                  label="Maximum queue length"
                  error={errors}
                  onChange={formHandler}
                  value={queue.maxlen}
                  min={0}
                  helperText="Maximum number of callers that can be in the queue at once. Leave empty or 0 for unlimited."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputToggle
                  id="autofill"
                  error={errors}
                  onChange={formHandler}
                  states={["no", "yes"]}
                  value={queue.autofill}
                  primary="Parallel member calling"
                  secondary="Let multiple members in the queue accept calls at the same time."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputToggle
                  id="joinEmpty"
                  error={errors}
                  onChange={formHandler}
                  states={["no", "yes"]}
                  value={queue.joinEmpty}
                  primary="Join empty queues"
                  secondary="Allow callers to join the queue when no members are in the queue."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputToggle
                  id="leaveWhenEmpty"
                  error={errors}
                  onChange={formHandler}
                  states={["no", "yes"]}
                  value={queue.leaveWhenEmpty}
                  primary="Leave empty queues"
                  secondary="Automatically remove callers from the queue when no members are in the queue."
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Overflow">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <OverflowDrawer
                  label="Overflow"
                  value={queue.overflow}
                  propertyName="overflow"
                  overflowHandler={(value) => formHandler("overflow", value)}
                  filter={{ exten: [queue.exten], custom: [], sound: [] }}
                  helperText="Set the destination if the queue does not pick up the call."
                />
              </Grid>
            </FormGroup>
          </>
        )}
        {tab === 2 && (
          <>
            <FormGroup title="Announcements">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputMedia
                  hasEmpty={null}
                  id="joinAnnounce"
                  label="Join announcement"
                  onChange={formHandler}
                  value={queue.joinAnnounce}
                  selectValue="id"
                  selects={soundStore.list}
                  getOptionLabel={(option) => option.name}
                  error={errors}
                  getSourceDependency={(option) => option?.token}
                  getSourceGeneration={(option) =>
                    `${urls.audio}${pbx.context}%2F${option.id}.${option.contentType}?alt=media&token=${option.token}`
                  }
                  helperText="Callers will hear this when they call a queue."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputMedia
                  hasEmpty="no"
                  id="announce"
                  label="Response announcement"
                  onChange={formHandler}
                  value={queue.announce}
                  selectValue="id"
                  selects={soundStore.list}
                  getOptionLabel={(option) => option.name}
                  error={errors}
                  getSourceDependency={(option) => option?.token}
                  getSourceGeneration={(option) =>
                    `${urls.audio}${pbx.context}%2F${option.id}.${option.contentType}?alt=media&token=${option.token}`
                  }
                  helperText="Members will hear this before answering a caller."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputMedia
                  hasEmpty={null}
                  id="periodicAnnounce"
                  label="Periodic announcement"
                  onChange={formHandler}
                  value={queue.periodicAnnounce}
                  selectValue="id"
                  selects={soundStore.list}
                  getOptionLabel={(option) => option.name}
                  error={errors}
                  getSourceDependency={(option) => option?.token}
                  getSourceGeneration={(option) =>
                    `${urls.audio}${pbx.context}%2F${option.id}.${option.contentType}?alt=media&token=${option.token}`
                  }
                  helperText="Callers will hear this while in the queue."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumeric
                  required={
                    queue.periodicAnnounce !== "0" && queue.periodicAnnounce !== 0 && queue.periodicAnnounce !== null
                  }
                  disabled={
                    queue.periodicAnnounce === "0" || queue.periodicAnnounce === 0 || queue.periodicAnnounce === null
                  }
                  id="periodicAnnounceFrequency"
                  label="Periodic announcement frequency"
                  onChange={formHandler}
                  value={queue.periodicAnnounceFrequency}
                  min={0}
                  endAdornment={
                    <InputAdornment position="end" sx={{ marginRight: 1 }}>
                      seconds
                    </InputAdornment>
                  }
                  error={errors}
                />
              </Grid>
            </FormGroup>
            <FormGroup title="System announcements">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputSelect
                  id="language"
                  label="Language"
                  error={errors}
                  onChange={formHandler}
                  value={queue.language}
                  selects={[
                    { value: "nl_BE", name: "Dutch (Belgium)" },
                    { value: "en_GB", name: "English (United Kingdom)" },
                    { value: "en_US", name: "English (United States)" },
                    { value: "fr_FR", name: "French" },
                  ]}
                  getOptionLabel={(option) => option.name}
                  helperText="The language of the holdtime and position announcements."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumeric
                  required={queue.announceHoldtime !== "no" || queue.announcePosition !== "no"}
                  disabled={queue.announceHoldtime === "no" && queue.announcePosition === "no"}
                  id="announceFrequency"
                  label="Announcement frequency"
                  onChange={formHandler}
                  value={queue.announceFrequency}
                  min={0}
                  endAdornment={
                    <InputAdornment position="end" sx={{ marginRight: 1 }}>
                      seconds
                    </InputAdornment>
                  }
                  helperText="Time between the end of a system announcement and the start of the next."
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumeric
                  required
                  id="minAnnounceFrequency"
                  label="Minimum announcement frequency"
                  onChange={formHandler}
                  value={queue.minAnnounceFrequency}
                  min={0}
                  endAdornment={
                    <InputAdornment position="end" sx={{ marginRight: 1 }}>
                      seconds
                    </InputAdornment>
                  }
                  helperText="Minimum amount of time between 2 system announcements."
                  error={errors}
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Hold time">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputSelect
                  id="announceHoldtime"
                  label="Announce hold time"
                  onChange={formHandler}
                  value={queue.announceHoldtime}
                  selects={[
                    { value: "no", name: "No" },
                    { value: "once", name: "Once" },
                    { value: "yes", name: "Yes" },
                  ]}
                  getOptionLabel={(option) => option.name}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputToggle
                  id="reportholdtime"
                  onChange={formHandler}
                  states={["no", "yes"]}
                  value={queue.reportholdtime}
                  primary="Report hold time"
                  secondary="Report the hold time to the member when the call is picked up."
                  error={errors}
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Position">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputSelect
                  id="announcePosition"
                  label="Announce position"
                  onChange={formHandler}
                  value={queue.announcePosition}
                  selects={[
                    { value: "no", name: "No" },
                    { value: "limit", name: "Limit" },
                    { value: "yes", name: "Yes" },
                  ]}
                  getOptionLabel={(option) => option.name}
                  primary="Announce the current position in the queue to callers."
                  error={errors}
                />
              </Grid>
              {queue.announcePosition === "limit" && (
                <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                  <InputNumeric
                    id="announcePositionLimit"
                    label="Position threshold"
                    onChange={formHandler}
                    value={queue.announcePositionLimit}
                    min={1}
                    helperText="The maximum position in the queue at which point the position will be announced."
                    error={errors}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputToggle
                  id="announceToFirstUser"
                  onChange={formHandler}
                  states={["no", "yes"]}
                  value={queue.announceToFirstUser}
                  primary="Play announcement to the first user"
                  secondary="While playing an announcement, it won't be possible for a member to connect to the first person until after the announcement finishes playing."
                  error={errors}
                />
              </Grid>
            </FormGroup>
          </>
        )}
        {tab === 3 && (
          <FormGroup
            title={`Members \u00b7 ${queue.members.length}`}
            actions={<QueueMembers queue={queue} queueDispatch={dispatch} />}
          >
            <Grid size={12}>
              {queue.members.length >= 1 ? (
                <>
                  <List>
                    {queue.members.map((member) => (
                      <MemberItem key={member.exten} member={member} queue={queue} queueDispatch={dispatch} />
                    ))}
                  </List>
                </>
              ) : (
                <Box
                  sx={[
                    {
                      borderRadius: 1,
                      gridColumn: "1 / -1",
                      alignItems: "center",
                      px: 1.75,
                      py: 1.5,
                      bgcolor: (theme) => (theme.palette.mode === "light" ? "#f8f8f8" : "#28282d"),
                    },
                  ]}
                >
                  <Typography variant="body2">This queue has no members...</Typography>
                </Box>
              )}
            </Grid>
          </FormGroup>
        )}
      </Stack>
    </Container>
  );
}

export default Queue;
